import { useCallback, useEffect, useState } from "react";
import type { CategoryDto, NamedInput, PermissionKey } from "@delta/shared";
import { useLang } from "../../i18n";
import { useSession } from "../../lib/session";
import { api } from "../../lib/api";
import { errorText, fieldErrorsOf } from "../../lib/errors";
import { Icon } from "../../components/Icon";
import { PageHead, Shell } from "../../components/Shell";
import { ListView, type Column } from "../../components/ListView";
import { Blocked, Confirm, Field, Modal } from "../../components/Modal";
import { useToast } from "../../components/Toast";
import { LockedButton, lockTag } from "../settings/common";

// Items › Categories and Items › Unit Config, ported from the prototype's bilingual name lists (erp-items.js
// nameList). Audited fields: Arabic and English names. The Items column counts the items that use the record; a
// record in use cannot be deleted. Both lists have the same shape, so one component serves them.

type Row = CategoryDto; // UnitDto has the same fields
interface Kind {
  path: string; route: string; title: string; newKey: string; editKey: string; cantKey: string; file: string;
  add: PermissionKey; update: PermissionKey; del: PermissionKey;
}
const KINDS = {
  category: { path: "/item-categories", route: "/item-Category", title: "categories", newKey: "newCategory", editKey: "editCategory", cantKey: "cantDeleteCategory", file: "categories", add: "itemCategoryAdd", update: "itemCategoryUpdate", del: "itemCategoryDelete" },
  unit: { path: "/units", route: "/unitsConfig", title: "unitConfig", newKey: "newUnit", editKey: "editUnit", cantKey: "cantDeleteUnit", file: "units", add: "itemUnitAdd", update: "itemUnitUpdate", del: "itemUnitDelete" }
} satisfies Record<string, Kind>;

function NameForm({ k, row, onClose, onSaved }: { k: Kind; row: Row | null; onClose(): void; onSaved(r: Row, created: boolean): void }) {
  const { t } = useLang();
  // A migrated record with a name missing (I-13) shows the other name in lists; here the missing one starts empty, so
  // it is typed, never copied from the other language.
  const [v, setV] = useState<NamedInput>(() => ({ nameAr: row && row.missingName !== "ar" ? row.nameAr : "", nameEn: row && row.missingName !== "en" ? row.nameEn : "" }));
  const [errs, setErrs] = useState<Record<string, string>>({});
  const [alert, setAlert] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (f: keyof NamedInput, x: string) => { setV((o) => ({ ...o, [f]: x })); setErrs((e) => ({ ...e, [f]: "" })); };
  async function save() {
    const e: Record<string, string> = {};
    if (!v.nameAr.trim()) e.nameAr = t("requiredField");
    if (!v.nameEn.trim()) e.nameEn = t("requiredField");
    setErrs(e);
    if (Object.keys(e).length) { setAlert(t("fixRequired")); return; }
    setBusy(true); setAlert(null);
    try { onSaved(row ? await api<Row>("PUT", `${k.path}/${row.id}`, v) : await api<Row>("POST", k.path, v), !row); }
    catch (err) { setErrs(fieldErrorsOf(t, err)); setAlert(errorText(t, err)); }
    finally { setBusy(false); }
  }
  return (
    <Modal title={t(row ? k.editKey : k.newKey)} onClose={onClose} onSubmit={save} alert={alert} busy={busy}>
      <Field label={t("nameAr")} req error={errs.nameAr} htmlFor="nf-ar"><input id="nf-ar" className="control" lang="ar" dir="rtl" value={v.nameAr} onChange={(e) => set("nameAr", e.target.value)} autoComplete="off" /></Field>
      <Field label={t("nameEn")} req error={errs.nameEn} htmlFor="nf-en"><input id="nf-en" className="control" lang="en" dir="ltr" value={v.nameEn} onChange={(e) => set("nameEn", e.target.value)} autoComplete="off" /></Field>
    </Modal>
  );
}

/** A name cell; a name missing since the migration shows the other language's name, marked as such. */
function MaybeMissing({ r, lang }: { r: Row; lang: "en" | "ar" }) {
  const { t } = useLang();
  const name = lang === "ar" ? r.nameAr : r.nameEn;
  if (r.missingName !== lang) return <span lang={lang} className={lang === "ar" ? "cell-name" : undefined}>{name}</span>;
  return <span lang={lang === "ar" ? "en" : "ar"} className="muted" title={t(lang === "en" ? "nameEnMissing" : "nameArMissing")} data-missing-name={lang}>
    {name} <span className="tag-addition">{t(lang === "en" ? "nameEnMissing" : "nameArMissing")}</span></span>;
}

function NameList({ kind }: { kind: keyof typeof KINDS }) {
  const k: Kind = KINDS[kind];
  const { t, L } = useLang();
  const s = useSession();
  const toast = useToast();
  const [rows, setRows] = useState<Row[] | null>(null);
  const [failed, setFailed] = useState<string | null>(null);
  const [filters, setFilters] = useState<Record<string, string>>({});
  const [editing, setEditing] = useState<Row | "new" | null>(null);
  const [deleting, setDeleting] = useState<Row | null>(null);
  const [blocked, setBlocked] = useState<Row | null>(null);

  const load = useCallback(async () => {
    setFailed(null);
    try { setRows(await api<Row[]>("GET", `${k.path}${filters.name ? "?q=" + encodeURIComponent(filters.name) : ""}`)); }
    catch (e) { setFailed(errorText(t, e)); }
  }, [k.path, filters.name, t]);
  useEffect(() => { setRows(null); void load(); }, [load]);

  async function doDelete(r: Row) {
    setDeleting(null);
    try { await api("DELETE", `${k.path}/${r.id}`); toast(t("deletedOk", { x: L(r) })); }
    catch (e) { toast(errorText(t, e)); }
    await load();
  }
  const columns: Column<Row>[] = [
    { k: "ar", label: t("nameAr"), cell: (r) => <MaybeMissing r={r} lang="ar" />, csv: (r) => r.nameAr },
    { k: "en", label: t("nameEn"), cell: (r) => <MaybeMissing r={r} lang="en" />, csv: (r) => r.nameEn },
    { k: "used", label: t("itemsCount"), end: true, cell: (r) => r.itemsCount, csv: (r) => r.itemsCount }
  ];
  const newBtn = s.can(k.add) ? <button className="btn btn-primary" onClick={() => setEditing("new")}><Icon name="plus" />{t(k.newKey)}</button>
    : <LockedButton label={t(k.newKey)} title={t("noPermissionFor", { p: k.add })} />;

  return (
    <Shell navKey={k.route}>
      <PageHead crumbs={[[t("home"), "/home"], [t("items")], [t(k.title)]]} title={t(k.title)} />
      <ListView rows={rows} error={failed} columns={columns} file={k.file} testId={`${k.file}-table`} onRefresh={() => void load()}
        search={[{ k: "name", label: t("name"), type: "text" }]} filters={filters} onFilters={setFilters}
        quick={(r) => `${r.nameEn} ${r.nameAr}`} newButton={newBtn}
        empty={{ icon: "box", title: t("noResults"), body: t("noResultsBody") }}
        actions={(r) => [
          { label: t("edit"), icon: "edit", run: () => setEditing(r), ...(s.can(k.update) ? {} : lockTag(t)) },
          { label: "", sep: true },
          // A record in use is kept: the prototype shows why instead of asking to confirm (the server refuses too).
          { label: t("delete"), icon: "trash", danger: true, run: () => (r.itemsCount ? setBlocked(r) : setDeleting(r)), ...(s.can(k.del) ? {} : lockTag(t)) }
        ]} />
      {editing && <NameForm k={k} row={editing === "new" ? null : editing} onClose={() => setEditing(null)}
        onSaved={(r, created) => { setEditing(null); toast(t(created ? "addedOk" : "updatedOk", { x: L(r) })); void load(); }} />}
      {deleting && <Confirm title={t("confirmDelete")} body={t("deleteWarn")} okLabel={t("yesSure")} onClose={() => setDeleting(null)} onOk={() => void doDelete(deleting)} />}
      {blocked && <Blocked title={t("cantDelete")} body={`${t(k.cantKey)} (${t("refCount_items", { n: blocked.itemsCount })})`} onClose={() => setBlocked(null)} />}
    </Shell>
  );
}

export const Categories = () => <NameList kind="category" />;
export const Units = () => <NameList kind="unit" />;
